// Static news fixture — Wave 2 stub data for the hot-zone popup (W2-B) and
// the Major News side panel (W2-C).
//
// Each row uses real outlets and plausible storylines so the UX surface is
// recognizable in screenshots. Linked signal ids are intentionally generic
// (sig-XXXX) — they don't need to match the live feed; the feed-side rules
// degrade gracefully when a linked id isn't present.
//
// The Inference orchestrator's connector replaces this file with a hot-cache
// pull off `/v1/news/feed` once that ships. The shape is identical (NewsItem
// Zod schema in `../types.ts`), so the swap is one import line.

import { credibilityScore } from '../credibility.js';
import type { NewsItem } from '../types.js';

/**
 * Helper: build a fixture row. `credibility_score` is computed via the stub
 * scorer so all rows pick up future tweaks to the band thresholds for free.
 */
function row(args: Omit<NewsItem, 'credibility_score'>): NewsItem {
  return {
    ...args,
    credibility_score: credibilityScore(args.source.name, args.title.length),
  };
}

function ago(hours: number, now: number): string {
  return new Date(now - hours * 60 * 60 * 1000).toISOString();
}

/**
 * Build a fresh fixture snapshot anchored to `now`. Used by the live hooks
 * so each refetch advances `published_at` against wall-clock time — the UI's
 * `relTime()` reads "5m / 1h / 2h" instead of going stale at module-load.
 *
 * The Inference orchestrator's connector replaces this with a hot-cache pull
 * off `/v1/news/feed`. Until then, this gives the rail a live-feed feel.
 */
export function getSampleNews(now: number = Date.now()): readonly NewsItem[] {
  return buildSampleNews(now);
}

function buildSampleNews(now: number): readonly NewsItem[] {
  return [
    row({
      id: 'news-001',
      title: 'Strikes hit logistics depot in eastern Ukraine, regional governor reports',
      summary:
        'Multiple munitions strikes reported overnight against a fuel and logistics depot. Local officials describe fires across an area of several hectares. Air-defense activity reported intermittently through the night.',
      url: 'https://www.reuters.com/world/europe/ukraine-strikes-depot-2026-05-13/',
      source: { name: 'Reuters', url: 'https://www.reuters.com' },
      published_at: ago(0.5, now),
      region: 'EU',
      linked_signal_ids: ['sig-conflict-eu-001', 'sig-conflict-eu-002'],
      severity: 88,
    }),
    row({
      id: 'news-002',
      title: 'OFAC adds 14 entities tied to dual-use semiconductor exports',
      summary:
        'New designations target shell companies routing legacy-node chips through third-country distributors. Treasury cites diversion to sanctioned defense end-users.',
      url: 'https://apnews.com/article/ofac-semiconductors-sanctions-2026',
      source: { name: 'Associated Press', url: 'https://apnews.com' },
      published_at: ago(1.2, now),
      region: 'NA',
      linked_signal_ids: ['sig-sanctions-na-001'],
      severity: 74,
    }),
    row({
      id: 'news-003',
      title: 'Red Sea shipping reroutes spike again as escort gap widens',
      summary:
        'Maritime tracking shows a 22% drop in northbound transits week-over-week. Insurers raised war-risk premiums for the second time this quarter.',
      url: 'https://www.bbc.com/news/world-middle-east-red-sea-2026',
      source: { name: 'BBC', url: 'https://www.bbc.com' },
      published_at: ago(2, now),
      region: 'ME',
      linked_signal_ids: ['sig-supply-me-001', 'sig-supply-me-002'],
      severity: 81,
    }),
    row({
      id: 'news-004',
      title: 'M6.4 quake offshore Sumatra; tsunami advisory in effect',
      summary:
        'USGS reports a magnitude-6.4 event at 32km depth. Indonesian authorities issued a regional advisory; no large-wave readings observed at the nearest gauges in the first hour.',
      url: 'https://www.aljazeera.com/news/2026/5/13/sumatra-quake',
      source: { name: 'Al Jazeera', url: 'https://www.aljazeera.com' },
      published_at: ago(3, now),
      region: 'AS',
      linked_signal_ids: ['sig-seismic-as-001'],
      severity: 70,
    }),
    row({
      id: 'news-005',
      title: 'Defense News: Air Force eyes new tanker buy under stretched FY27 topline',
      summary:
        'Service leaders briefed lawmakers on a phased tanker recapitalization that defers a sixth-gen demonstrator slip. Schedule risk flagged on engine integration testbed.',
      url: 'https://www.defensenews.com/air/2026/05/13/tanker-fy27',
      source: { name: 'Defense News', url: 'https://www.defensenews.com' },
      published_at: ago(4, now),
      region: 'NA',
      linked_signal_ids: [],
      severity: 55,
    }),
    row({
      id: 'news-006',
      title:
        'ISW: Battlefield assessment, May 12 — slow southern advance, no operationally significant changes',
      summary:
        'Institute for the Study of War assesses incremental tactical movement on the southern axis with no operationally significant changes to control of populated areas in the last 24 hours.',
      url: 'https://www.understandingwar.org/backgrounder/may-12-2026',
      source: { name: 'ISW', url: 'https://www.understandingwar.org' },
      published_at: ago(5, now),
      region: 'EU',
      linked_signal_ids: ['sig-conflict-eu-003'],
      severity: 78,
    }),
    row({
      id: 'news-007',
      title: 'Janes: Coastal radar refresh accelerates across two Indo-Pacific allies',
      summary:
        'Procurement filings show two allied navies advancing coastal-surveillance radar refresh on a compressed schedule. Bid windows shorten by an estimated 6–10 weeks.',
      url: 'https://www.janes.com/defence-news/2026/05/13/coastal-radar-pacific',
      source: { name: "Jane's", url: 'https://www.janes.com' },
      published_at: ago(6, now),
      region: 'AS',
      linked_signal_ids: ['sig-policy-as-001'],
      severity: 62,
    }),
    row({
      id: 'news-008',
      title: 'AFP: Niger junta restricts uranium concentrate movement through northern corridor',
      summary:
        'Authorities suspended commercial transit permits for yellowcake convoys pending review. Western utilities exposed to Niger-sourced fuel cycle disclose contingency assessments.',
      url: 'https://www.afp.com/en/news/niger-uranium-2026-05-13',
      source: { name: 'AFP', url: 'https://www.afp.com' },
      published_at: ago(8, now),
      region: 'AF',
      linked_signal_ids: ['sig-supply-af-001'],
      severity: 76,
    }),
    row({
      id: 'news-009',
      title: 'NOAA hurricane outlook: above-normal 2026 Atlantic season, 17–22 named storms',
      summary:
        'Outlook cites very-warm Atlantic SSTs and an ENSO-neutral state. Energy and shipping sectors begin pre-season repositioning of crews and assets.',
      url: 'https://www.noaa.gov/news-release/2026-atlantic-hurricane-outlook',
      source: { name: 'NOAA', url: 'https://www.noaa.gov' },
      published_at: ago(11, now),
      region: 'NA',
      linked_signal_ids: ['sig-weather-na-001'],
      severity: 50,
    }),
    row({
      id: 'news-010',
      title: 'Reuters: Major rare-earth miner halts shipments after permit dispute',
      summary:
        'Filings cite a regional permit ruling. Spot-market neodymium prices opened up 4%. Two downstream defense primes hold inventory buffers under 90 days.',
      url: 'https://www.reuters.com/markets/commodities/rare-earth-halt-2026',
      source: { name: 'Reuters', url: 'https://www.reuters.com' },
      published_at: ago(13, now),
      region: 'OC',
      linked_signal_ids: ['sig-supply-oc-001'],
      severity: 72,
    }),
    row({
      id: 'news-011',
      title: 'EU sanctions package targets logistics network supporting sanctioned tech transfers',
      summary:
        'New Council Implementing Regulation extends listings to 9 freight forwarders and 4 financial intermediaries. EU spokesperson: "the network was the throughline."',
      url: 'https://www.reuters.com/world/europe/eu-sanctions-package-2026-05-13',
      source: { name: 'Reuters', url: 'https://www.reuters.com' },
      published_at: ago(15, now),
      region: 'EU',
      linked_signal_ids: ['sig-sanctions-eu-001', 'sig-sanctions-eu-002'],
      severity: 67,
    }),
    row({
      id: 'news-012',
      title: 'BBC: Polar low intensifies over North Sea, oil platforms move to limited ops',
      summary:
        'A short-fuse polar low intensified faster than forecast models predicted. Operators on three North Sea fields moved to limited-operations posture overnight.',
      url: 'https://www.bbc.com/weather/north-sea-polar-low-2026',
      source: { name: 'BBC', url: 'https://www.bbc.com' },
      published_at: ago(17, now),
      region: 'EU',
      linked_signal_ids: ['sig-weather-eu-001'],
      severity: 58,
    }),
    row({
      id: 'news-013',
      title: 'AP: GSA finalizes cyber-services contract vehicle, $12B ceiling over 5 years',
      summary:
        'Final on-ramp procedures published. Industry trade groups praised the simplified small-business pool; civil-society groups raised supply-chain transparency concerns.',
      url: 'https://apnews.com/article/gsa-cyber-contract-vehicle-2026',
      source: { name: 'Associated Press', url: 'https://apnews.com' },
      published_at: ago(20, now),
      region: 'NA',
      linked_signal_ids: [],
      severity: 42,
    }),
    row({
      id: 'news-014',
      title: 'AIS data: dark-fleet rendezvous off east Africa coast doubles year-over-year',
      summary:
        'A maritime analytics group cross-referenced AIS gaps with optical satellite captures. The pattern correlates with sanctioned-cargo transshipment along established corridors.',
      url: 'https://www.reuters.com/world/africa/dark-fleet-east-africa-2026',
      source: { name: 'Reuters', url: 'https://www.reuters.com' },
      published_at: ago(22, now),
      region: 'AF',
      linked_signal_ids: ['sig-anomaly-af-001'],
      severity: 71,
    }),
    row({
      id: 'news-015',
      title:
        'Al Jazeera: Cross-border firefight reported along contested frontier; casualties unverified',
      summary:
        'Sporadic small-arms and indirect-fire exchanges reported through the early morning. Both sides issued statements; independent verification limited by access restrictions.',
      url: 'https://www.aljazeera.com/news/2026/5/12/border-firefight',
      source: { name: 'Al Jazeera', url: 'https://www.aljazeera.com' },
      published_at: ago(25, now),
      region: 'AS',
      linked_signal_ids: ['sig-conflict-as-001'],
      severity: 84,
    }),
    row({
      id: 'news-016',
      title: 'AFP: Brazilian armed-forces tender for tactical UAVs enters bid stage',
      summary:
        'Initial market consultation closed. Up to 18 short-listed vendors invited to the next stage. Industrial-participation requirements significant.',
      url: 'https://www.afp.com/en/news/brazil-uav-tender-2026',
      source: { name: 'AFP', url: 'https://www.afp.com' },
      published_at: ago(30, now),
      region: 'SA',
      linked_signal_ids: [],
      severity: 35,
    }),
    row({
      id: 'news-017',
      title:
        'Reuters: South China Sea — additional coast-guard incursions reported, fishing fleet tracked',
      summary:
        'Manila reported additional coast-guard incursions near disputed reefs. Independent ship-tracking shows a coordinated fishing-fleet deployment in adjacent waters.',
      url: 'https://www.reuters.com/world/asia-pacific/south-china-sea-2026-05-13',
      source: { name: 'Reuters', url: 'https://www.reuters.com' },
      published_at: ago(33, now),
      region: 'AS',
      linked_signal_ids: ['sig-policy-as-002', 'sig-conflict-as-002'],
      severity: 79,
    }),
    row({
      id: 'news-018',
      title: "Jane's: New ground-launched cruise-missile variant unveiled at regional exercise",
      summary:
        'Imagery analysts assess the new variant as a road-mobile derivative with extended-range fuel. Doctrine implications still under discussion.',
      url: 'https://www.janes.com/defence-news/2026/05/13/glcm-variant',
      source: { name: "Jane's", url: 'https://www.janes.com' },
      published_at: ago(40, now),
      region: 'AS',
      linked_signal_ids: ['sig-policy-as-003'],
      severity: 65,
    }),
  ];
}

/**
 * Backward-compat snapshot — evaluated once at module load. Tests, the map
 * popup, and other surfaces that don't need wall-clock freshness can keep
 * importing this. The live hooks call `getSampleNews()` per fetch instead.
 */
export const SAMPLE_NEWS: readonly NewsItem[] = buildSampleNews(Date.now());
